import { DeliveryWindowsColumn } from '@app/components/orders/OrdersList/OrdersListTable/DeliveryWindowsColumn';
import { OrderActionsColumn } from '@app/components/orders/OrdersList/OrdersListTable/OrderActionsColumn';
import { OrderNumberColumn } from '@app/components/orders/OrdersList/OrdersListTable/OrderNumberColumn';
import { Link } from '@app/components/various/Link';
import { StatusCell } from '@app/components/various/Table';
import { BREAKPOINTS } from '@app/constants/breakpoints';
import { getIsSeller } from '@app/ducks';
import { compiledPaths } from '@app/paths';
import { isDefined } from '@app/utils/is';
import { renderSentryMaskedText } from '@app/utils/monitoring';
import { ACTION_COLUMN_WIDTH, getUpdatedStateValue, renderMaskedCell, renderStandardCell } from '@app/utils/table';
import { createColumnHelper, getCoreRowModel, OnChangeFn, SortingState } from '@tanstack/react-table';
import { DeliveryWindow, Order } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useAmplitude } from '../../analytics/useAmplitude';
import { usePaginationHandler } from '../table/usePaginationHandler';
import { useTable } from '../table/useTable';
import { useTableColumnsConfiguration } from '../table/useTableColumnsConfiguration';
import { useIsScreenSize } from '../useIsScreenSize';
import { getFieldBySortKey, SortOrder } from '../useOrdersSorting';
import { useOrderStatusLabels } from '../useOrderStatusLabels';

const columnHelper = createColumnHelper<Order.Essentials>();

interface Props {
  deliveryWindows: DeliveryWindow.Mixed[];
  orders?: Order.Essentials[];
  sortOrder: SortOrder;
  page: number;
  handleSortChange: (sortOrder: SortOrder) => void;
  handlePageChange: (page: number) => void;
  handleCopyToNewSelection: (order: Order.Essentials) => void;
  handleLinesheetDownload: (order: Order.Essentials) => void;
}

export const useOrdersListTable = ({
  deliveryWindows,
  orders = [],
  page,
  sortOrder = [],
  handleSortChange,
  handlePageChange,
  handleCopyToNewSelection,
  handleLinesheetDownload,
}: Props) => {
  const { t } = useTranslation(['accounts', 'buyers', 'common', 'shipping', 'totals', 'orders', 'checkout']);
  const isSeller = useSelector(getIsSeller);
  const [sorting, setSorting] = React.useState<SortingState>(
    sortOrder.map(sortItem => ({ desc: sortItem.order === 'desc', id: getFieldBySortKey(sortItem.field) })),
  );

  const orderStatusLabels = useOrderStatusLabels();
  const isMobile = useIsScreenSize(BREAKPOINTS.LG);
  const { columnsVisibility } = useTableColumnsConfiguration({ tableName: 'ordersTable' });
  const { pagination, handlePaginationChange } = usePaginationHandler({ onPageChange: handlePageChange, page });
  const { trackEvent } = useAmplitude();

  const getDeliveryWindowByIds = React.useCallback(
    (deliveryWindowsIds: DeliveryWindow.Id[]) => {
      return deliveryWindowsIds
        .map(delwinId => deliveryWindows.find(({ deliveryWindow }) => delwinId === deliveryWindow))
        .filter(isDefined);
    },
    [deliveryWindows],
  );

  const columns = React.useMemo(() => {
    return [
      columnHelper.accessor('status', {
        cell: info => <StatusCell status={info.getValue()} tooltip={orderStatusLabels[info.getValue()]} />,
        enableSorting: false,
        header: t('common:status'),
        meta: {
          align: 'center',
          noWrap: true,
          width: 50,
        },
      }),
      columnHelper.accessor('orderNumber', {
        cell: info => <OrderNumberColumn order={info.row.original} />,
        enableHiding: false,
        header: t('common:number_short'),
      }),
      columnHelper.accessor('accountName', {
        cell: info => {
          const orderRow = info.row.original;
          const path = compiledPaths.ACCOUNT_DETAILS({ accountId: orderRow.account });

          const handleClick = () => {
            trackEvent({ name: 'ordersTable.link.accountName.click' });
          };

          return isSeller ?
              <Link to={path} data-sentry-mask onClick={handleClick}>
                {renderSentryMaskedText(info.getValue())}
              </Link>
            : renderSentryMaskedText(orderRow.accountName);
        },
        header: t('accounts:account_one'),
        id: 'account',
      }),
      columnHelper.accessor('buyer', {
        cell: info => {
          const orderRow = info.row.original;

          return <span data-sentry-mask>{renderSentryMaskedText(`${orderRow.buyerFirstName} ${orderRow.buyerLastName}`)}</span>;
        },
        header: t('buyers:buyer_one'),
        id: 'buyer',
      }),
      columnHelper.accessor('poNumber', {
        cell: renderStandardCell,
        header: t('common:po_no'),
      }),
      columnHelper.accessor('shippingCountry', {
        cell: renderMaskedCell,
        enableSorting: false,
        header: t('shipping:shipping_country'),
      }),
      columnHelper.accessor('deliveryWindows', {
        cell: info => {
          const orderDeliveryWindows = info.row.original.deliveryWindows;

          return <DeliveryWindowsColumn deliveryWindows={getDeliveryWindowByIds(orderDeliveryWindows)} />;
        },
        enableSorting: false,
        header: t('common:delivery_window_one'),
        id: 'delivery',
        meta: {
          isCollapsible: true,
          width: '100%',
        },
      }),
      columnHelper.accessor('orderDate', {
        cell: renderStandardCell,
        enableSorting: false,
        header: t('checkout:order_date'),
        meta: {
          minWidth: 150,
        },
      }),
      columnHelper.accessor('totals', {
        cell: info => info.getValue().itemsCount,
        enableHiding: !isMobile,
        header: t('common:unit_other'),
        id: 'itemsCount',
        meta: {
          align: 'right',
          noWrap: true,
        },
      }),
      columnHelper.accessor('totals', {
        cell: info => info.getValue().grandTotalWithoutTax,
        header: t('totals:total_no_tax'),
        id: 'total',
        meta: {
          align: 'right',
          noWrap: true,
        },
      }),
      columnHelper.display({
        cell: info => {
          return (
            <OrderActionsColumn
              order={info.row.original}
              onCopyToNewSelection={handleCopyToNewSelection}
              onLinesheetDownload={handleLinesheetDownload}
            />
          );
        },
        enableHiding: false,
        id: 'order',
        meta: {
          skeletonType: 'actions',
          width: ACTION_COLUMN_WIDTH,
        },
      }),
    ];
  }, [t, isMobile, isSeller, trackEvent, getDeliveryWindowByIds]);

  const handleSortingChange: OnChangeFn<SortingState> = updaterOrValue => {
    const newSorting = getUpdatedStateValue(updaterOrValue, sorting);

    handleSortChange(newSorting.map(sortItem => ({ field: sortItem.id, order: sortItem.desc ? 'desc' : 'asc' })));
    setSorting(updaterOrValue);
  };

  const table = useTable(
    {
      columns,
      data: orders,
      getCoreRowModel: getCoreRowModel(),
      manualPagination: true,
      manualSorting: true,
      onPaginationChange: handlePaginationChange,
      onSortingChange: handleSortingChange,
      state: {
        pagination,
        sorting,
      },
    },
    columnsVisibility,
  );

  return {
    columnsVisibility,
    pagination,
    table,
  };
};
